import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Droplet, Calendar, Sparkles, Share2, Bell } from "lucide-react";
import PhoneR3FSafe from "../components/hero3d/PhoneR3FSafe";

/**
 * Deep-dive story page for Cycle Tracking.
 * Scroll-driven cinematic sequence: 5 chapters, sticky 3D phone that reacts to scroll progress.
 */

const CHAPTERS = [
    {
        eyebrow: "Chapter 01",
        title: "Log a moment. Learn a rhythm.",
        body: "Tap your period start, log a symptom, or record how you slept. FemCare quietly turns those daily moments into a personal, evolving cycle map.",
        Icon: Droplet,
    },
    {
        eyebrow: "Chapter 02",
        title: "See your whole month at a glance.",
        body: "A beautifully clear calendar that colour-codes your period, your fertile window and ovulation — so you can plan around your body, not around a guess.",
        Icon: Calendar,
    },
    {
        eyebrow: "Chapter 03",
        title: "Predictions that get sharper each cycle.",
        body: "Our adaptive engine learns from every log. The more you use FemCare, the tighter and more personal your next-period and fertile-window predictions become.",
        Icon: Sparkles,
    },
    {
        eyebrow: "Chapter 04",
        title: "Gentle nudges, exactly when you need them.",
        body: "Discreet reminders for your period, ovulation and pill — quietly designed so nothing on your lock screen ever reads like a headline.",
        Icon: Bell,
    },
    {
        eyebrow: "Chapter 05",
        title: "Your cycle, exported beautifully.",
        body: "Turn any month into a private PDF wellness report you can save, print or share with someone you trust — no accounts, no trackers, just your data.",
        Icon: Share2,
    },
];

function Chapter({ chapter, index }) {
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [80, -80]);
    const opacity = useTransform(scrollYProgress, [0.05, 0.25, 0.75, 0.95], [0, 1, 1, 0]);

    return (
        <section
            ref={ref}
            className="relative min-h-[80vh] flex items-center px-6 lg:px-12"
            data-testid={`cycle-chapter-${index + 1}`}
        >
            <motion.div
                style={{ y, opacity }}
                className="max-w-xl"
            >
                <p className="text-[11px] uppercase tracking-[0.32em] text-femcare-pinkDeep/80 font-bold mb-4">
                    {chapter.eyebrow}
                </p>
                <div className="flex items-center gap-3 mb-5">
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FCE4EC,#F9A8D4)" }}>
                        <chapter.Icon className="w-6 h-6 text-femcare-pinkDeep" />
                    </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-femcare-ink leading-[1.05] tracking-[-0.03em]">
                    {chapter.title}
                </h2>
                <p className="mt-5 text-base lg:text-lg text-femcare-inkSoft leading-relaxed">
                    {chapter.body}
                </p>
            </motion.div>
        </section>
    );
}

export default function FeatureCycle() {
    const reduced = useReducedMotion();
    const containerRef = useRef(null);
    const scrollProgress = useRef(0);

    // Wire vertical scroll into the 3D phone's own scrollProgress ref
    const { scrollYProgress } = useScroll({ target: containerRef });
    React.useEffect(() => {
        const unsub = scrollYProgress.on("change", (v) => {
            scrollProgress.current = v;
        });
        return () => unsub();
    }, [scrollYProgress]);

    // Body gradient lock while on this page
    React.useEffect(() => {
        const prev = document.body.style.background;
        document.body.style.background = "#FFFFFF";
        return () => { document.body.style.background = prev; };
    }, []);

    return (
        <div
            ref={containerRef}
            data-testid="feature-cycle-page"
            className="relative overflow-x-hidden"
            style={{
                background: `
                    radial-gradient(ellipse 55% 40% at 20% 10%, rgba(247,225,232,0.5), transparent 60%),
                    radial-gradient(ellipse 45% 40% at 90% 80%, rgba(220,207,232,0.45), transparent 60%),
                    #FFFFFF
                `,
            }}
        >
            {/* HERO */}
            <section className="relative pt-32 pb-16 px-6 lg:px-12 overflow-hidden">
                {/* Floating pearls */}
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                    {[
                        { s: 80, t: "18%", l: "8%",  d: 0 },
                        { s: 42, t: "22%", l: "62%", d: 1 },
                        { s: 110, t: "60%", l: "85%", d: 2 },
                        { s: 55, t: "78%", l: "12%", d: 0.6 },
                    ].map((p, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                width: p.s, height: p.s, top: p.t, left: p.l,
                                background: "radial-gradient(circle at 32% 28%, #ffffff 0%, #fce4ec 30%, #d4a0c0 100%)",
                                boxShadow: `inset -${p.s * 0.08}px -${p.s * 0.12}px ${p.s * 0.2}px rgba(255,255,255,0.5), 0 ${p.s * 0.15}px ${p.s * 0.5}px rgba(143,99,141,0.2)`,
                            }}
                            animate={reduced ? {} : { y: [0, -14, 0] }}
                            transition={{ duration: 7 + i, repeat: Infinity, ease: "easeInOut", delay: p.d }}
                        />
                    ))}
                </div>

                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-[11px] uppercase tracking-[0.32em] text-femcare-pinkDeep font-bold mb-6"
                    >
                        FemCare · Cycle Tracking
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-femcare-ink leading-[1.02] tracking-[-0.035em]"
                    >
                        Your cycle.
                        <br />
                        <span style={{
                            background: "linear-gradient(135deg,#EC4899 0%,#DB2777 60%,#8f638d 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}>
                            In beautiful clarity.
                        </span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="mt-6 text-lg text-femcare-inkSoft max-w-xl mx-auto leading-relaxed"
                    >
                        Adaptive predictions, gentle reminders, and a calendar that quietly gets to know your body — one log at a time.
                    </motion.p>
                </div>
            </section>

            {/* STORY GRID — sticky phone + scrolling chapters */}
            <section className="relative">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* LEFT — sticky 3D phone */}
                    <div className="hidden lg:block">
                        <div className="sticky top-24 h-[calc(100vh-6rem)] flex items-center justify-center">
                            <div className="w-full h-[600px] max-w-[420px]">
                                <PhoneR3FSafe screenSrc="/femcare-ui-cycle.jpg" scrollProgress={scrollProgress} />
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — chapters */}
                    <div>
                        {CHAPTERS.map((c, i) => (
                            <Chapter key={i} chapter={c} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Mobile-only phone teaser (since sticky column is hidden on mobile) */}
            <section className="lg:hidden px-6 pb-16">
                <div className="w-full h-[560px]">
                    <PhoneR3FSafe screenSrc="/femcare-ui-cycle.jpg" forceFallback />
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="px-6 lg:px-12 pt-8 pb-32">
                <div className="max-w-4xl mx-auto rounded-[2rem] p-10 lg:p-16 text-center text-white relative overflow-hidden"
                    style={{ background: "linear-gradient(135deg,#a87aa6 0%,#DB2777 60%,#EC4899 100%)" }}
                    data-testid="cycle-cta"
                >
                    <p className="font-script text-xl md:text-2xl text-white/85 mb-3">start the story</p>
                    <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.03em]">
                        Know your body.
                        <br />
                        <span className="italic font-light">Every single cycle.</span>
                    </h2>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <a
                            href="/app/signup"
                            className="inline-flex items-center gap-2 bg-white text-femcare-purple rounded-full px-7 py-3.5 font-semibold hover:scale-105 transition-transform"
                            data-testid="cycle-cta-primary"
                        >
                            Get FemCare
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <a
                            href="/features"
                            className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white border border-white/40 hover:bg-white/10 transition"
                            data-testid="cycle-cta-secondary"
                        >
                            Explore all features
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
